import { addDays, addMonths, addYears, subDays, subMonths, subYears } from "date-fns"
import { TreeGridTranslations } from "../../hooks/use-translations"
import { GanttZoom } from "../../types"

type MakeZoomArgs = {
  translations: TreeGridTranslations
  enableTimeComponent: boolean
  leftEdgeRefDate?: Date
  rightEdgeRefDate?: Date
}

export const makeZooms = ({
  translations,
  enableTimeComponent,
  leftEdgeRefDate = new Date(),
  rightEdgeRefDate = new Date(),
}: MakeZoomArgs) => {
  const {
    zoomMenuHoursAndQuarters,
    zoomMenuDaysAndHours,
    zoomMenuWeeksAndHalfDays,
    zoomMenuWeeksAndDays,
    zoomMenuMonthsAndWeeks,
    zoomMenuMonthsWeeksAndDays,
    zoomMenuQuartersAndMonths,
    zoomMenuHalvesAndMonths,
    zoomMenuYearsAndQuarters,
  } = translations

  const zoomNames = {
    hoursAndQuarters: zoomMenuHoursAndQuarters,
    daysAndHours: zoomMenuDaysAndHours,
    weeksAndHalfDays: zoomMenuWeeksAndHalfDays,
    weeksAndDays: zoomMenuWeeksAndDays,
    monthsWeeksAndDays: zoomMenuMonthsWeeksAndDays,
    quartersAndMonths: zoomMenuQuartersAndMonths,
    yearsAndQuarters: zoomMenuYearsAndQuarters,
    monthsAndWeeks: zoomMenuMonthsAndWeeks,
    halvesAndMonths: zoomMenuHalvesAndMonths,
  }

  const sevenDaysBackward = subDays(leftEdgeRefDate, 7).getTime()
  const twentyDaysBackward = subDays(leftEdgeRefDate, 20).getTime()
  const sixMonthsBackward = subMonths(leftEdgeRefDate, 6).getTime()
  const oneYearBackward = subYears(leftEdgeRefDate, 1).getTime()
  const twoYearsBackward = subYears(leftEdgeRefDate, 2).getTime()
  const tenYearsBackward = subYears(leftEdgeRefDate, 10).getTime()

  const sevenDaysForward = addDays(rightEdgeRefDate, 7).getTime()
  const twentyDaysForward = addDays(rightEdgeRefDate, 20).getTime()
  const sixMonthsForward = addMonths(rightEdgeRefDate, 6).getTime()
  const oneYearForward = addYears(rightEdgeRefDate, 1).getTime()
  const threeYearsForward = addYears(rightEdgeRefDate, 3).getTime()
  const tenYearsForward = addYears(rightEdgeRefDate, 10).getTime()

  const zoomList: GanttZoom[] = [
    {
      Name: zoomNames.yearsAndQuarters,
      GanttUnits: "M3",
      GanttDragUnits: enableTimeComponent ? "h12" : "d",
      lagUnit: "d",
      GanttHeader1: "y#yyyy",
      GanttHeader2: "M3#MMMMM",
      GanttWidth: 64,
      GanttChartMaxStart: tenYearsBackward,
      GanttChartMinEnd: tenYearsForward,
    },
    {
      Name: zoomNames.halvesAndMonths,
      GanttUnits: "M",
      GanttDragUnits: enableTimeComponent ? "h12" : "d",
      lagUnit: "d",
      GanttHeader1: 'M6#MMMMMM"." yyyy',
      GanttHeader2: "M#MM",
      GanttChartMaxStart: tenYearsBackward,
      GanttChartMinEnd: tenYearsForward,
    },
    {
      Name: zoomNames.quartersAndMonths,
      GanttUnits: "M",
      GanttDragUnits: enableTimeComponent ? "h12" : "d",
      lagUnit: "d",
      GanttHeader1: "M3#MMMMM yyyy",
      GanttHeader2: "M#MMM",
      GanttWidth: 64,
      GanttChartMaxStart: twoYearsBackward,
      GanttChartMinEnd: threeYearsForward,
    },
    {
      Name: zoomNames.monthsWeeksAndDays,
      GanttUnits: "d",
      GanttDragUnits: enableTimeComponent ? "h12" : "d",
      lagUnit: "d",
      GanttLastUnit: enableTimeComponent ? "" : "d",
      GanttHeader1: "M#MMMM yyyy",
      GanttHeader2: `w1#"${translations.shortWeekLabel} "dddddddd`,
      GanttHeader3: 'd#"<span class=ganttDayHeader>"DDDDDD<br>DDDDDDD"</span>"',
      GanttHeaderHeight2: 12,
      GanttHeaderHeight3: 32,
      GanttWidth: 10,
      GanttChartMaxStart: oneYearBackward,
      GanttChartMinEnd: oneYearForward,
    },
    {
      Name: zoomNames.weeksAndDays,
      GanttUnits: "d",
      GanttDragUnits: enableTimeComponent ? "h" : "d",
      lagUnit: "d",
      GanttLastUnit: enableTimeComponent ? "" : "d",
      GanttHeader1: `w1#"${translations.shortWeekLabel} "dddddddd, MMM yyyy`,
      GanttHeader2: 'd#"<span class=ganttDayHeader>"dd"</span>"',
      GanttHeader3: 'd#"<span class=ganttDayHeader>"ddddd"</span>"',
      GanttWidth: 16,
      GanttChartMaxStart: oneYearBackward,
      GanttChartMinEnd: oneYearForward,
    },
    {
      Name: zoomNames.weeksAndHalfDays,
      GanttUnits: "h3",
      GanttDragUnits: "m15",
      lagUnit: "h",
      GanttHeader1: "d#ddd, dd MMM yy",
      GanttHeader2: "h3#HH",
      GanttWidth: 24,
      GanttChartMaxStart: sixMonthsBackward,
      GanttChartMinEnd: sixMonthsForward,
    },
    {
      Name: zoomNames.daysAndHours,
      GanttUnits: "m15",
      lagUnit: "h",
      GanttHeader1: "h12#ddd, dd MMM yy",
      GanttHeader2: "h#HH",
      GanttWidth: 24,
      GanttChartMaxStart: twentyDaysBackward,
      GanttChartMinEnd: twentyDaysForward,
    },
    {
      Name: zoomNames.hoursAndQuarters,
      GanttUnits: "m5",
      lagUnit: "m",
      GanttHeader1: "h3#ddd, dd MMM yy",
      GanttHeader2: "h# hh tt",
      GanttHeader3: "m15#mm",
      GanttWidth: 24,
      GanttChartMaxStart: sevenDaysBackward,
      GanttChartMinEnd: sevenDaysForward,
    },
  ]
  const filteredZoomList = zoomList.filter((zoom) => {
    if (!enableTimeComponent) {
      return (
        zoom.Name !== zoomNames.daysAndHours &&
        zoom.Name !== zoomNames.hoursAndQuarters &&
        zoom.Name !== zoomNames.weeksAndHalfDays
      )
    } else {
      return true
    }
  })

  return {
    defaultZoom: zoomNames.weeksAndDays,
    zoomNames,
    zoomList: filteredZoomList,
  }
}
